
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** shadcn-style className combiner: merge Tailwind classes safely. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format integer cents as BRL — rule 6: cents end-to-end, divide only at render. */
export function formatBRLCents(cents: number): string {
  return formatBRL(cents / 100);
}

/** Format a number as Brazilian Real currency (R$ 1.234,00). */
export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Minor-unit factor for an ISO 4217 code. Most currencies use 100 (cents); zero-decimal
 * ones (JPY, KRW, … — same set Stripe uses) store the major unit as the integer, so the
 * factor is 1. Multi-market money stays integer end-to-end; only the scale changes.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV',
  'XAF', 'XOF', 'XPF',
]);

export function currencyMinorFactor(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 1 : 100;
}

/** Market-priced surfaces (multi-market fees/plans): integer minor units in the given ISO
 *  4217 currency, formatted for the given locale (CA$ 250,00 / US$120.00 / ¥1,000). BRL-only
 *  call sites keep using formatBRL/formatBRLCents. */
export function formatMoney(cents: number, currency: string, locale = 'pt-BR'): string {
  const factor = currencyMinorFactor(currency);
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / factor);
  } catch {
    // Unknown/invalid currency code (admin typo): degrade to a plain amount + code.
    return `${(cents / factor).toFixed(factor === 1 ? 0 : 2)} ${currency}`;
  }
}

/** Display-only mirror of the API's `slugify` (`solidarity.service.ts`): shows an admin the
 *  URL a title will produce before saving. The server still derives the stored slug, so a
 *  drift here costs a preview, never a wrong link. */
export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

/** Structured address → a single readable line (empty parts dropped). */
export function formatAddress(
  a: { street?: string; number?: string; complement?: string | null; district?: string; city?: string; state?: string; cep?: string } | null | undefined,
): string {
  if (!a) return '';
  const streetLine = [a.street, a.number].filter((s) => s?.trim()).join(', ');
  const cityLine = [a.city, a.state].filter((s) => s?.trim()).join('/');
  return [streetLine, a.complement, a.district, cityLine, a.cep]
    .filter((s) => s?.trim())
    .join(' · ');
}
